import { useEffect, useRef } from 'react';

const GSI_SCRIPT_ID = 'google-identity-services';
const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const LOAD_ERROR_MESSAGE = 'Google 登入服務載入失敗，請確認網路連線後重新整理畫面';

/**
 * 載入 Google Identity Services 用戶端程式庫
 * 只在登入頁載入，避免全站都相依外部指令碼
 * @returns {Promise<void>}
 */
const loadGsiScript = () =>
    new Promise((resolve, reject) => {
        // 已載入完成
        if (window.google && window.google.accounts && window.google.accounts.id) {
            resolve();
            return;
        }

        const handleError = () => reject(new Error(LOAD_ERROR_MESSAGE));
        const existedScript = document.getElementById(GSI_SCRIPT_ID);

        // 載入中 (例如 React 嚴格模式重複掛載)，掛上事件等待結果即可
        if (existedScript) {
            existedScript.addEventListener('load', () => resolve());
            existedScript.addEventListener('error', handleError);
            return;
        }

        const script = document.createElement('script');
        script.id = GSI_SCRIPT_ID;
        script.src = GSI_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = handleError;

        document.head.appendChild(script);
    });

/**
 * Google 登入按鈕
 * 依 Google 品牌規範，按鈕須由 Google Identity Services 自行繪製
 * @param {Object} props
 * @param {String} props.clientId - OAuth 用戶端代碼，空值時不初始化
 * @param {Function} props.onCredential - 取得 ID Token 後的處理 (credential) => void
 * @param {Function} props.onError - 載入或初始化失敗的處理 (message) => void
 * @returns {Object} 按鈕容器的 ref
 */
const useGoogleSignIn = ({ clientId, onCredential, onError }) => {
    const buttonRef = useRef(null);
    // 以 ref 保存回呼，避免回呼重建時重新初始化 Google 用戶端
    const onCredentialRef = useRef(onCredential);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onCredentialRef.current = onCredential;
        onErrorRef.current = onError;
    });

    useEffect(
        () => {
            if (!clientId) return undefined;

            let isCancelled = false;

            loadGsiScript()
                .then(() => {
                    if (isCancelled || !buttonRef.current) return;

                    window.google.accounts.id.initialize({
                        client_id: clientId,
                        // popup：在彈出視窗完成 Google 驗證，本頁不會離開也不會重新載入
                        ux_mode: 'popup',
                        auto_select: false,
                        cancel_on_tap_outside: true,
                        callback: (response) => {
                            if (response && response.credential) {
                                onCredentialRef.current(response.credential);
                            }
                        },
                    });

                    window.google.accounts.id.renderButton(buttonRef.current, {
                        type: 'standard',
                        theme: 'outline',
                        size: 'large',
                        shape: 'rectangular',
                        text: 'signin_with',
                        logo_alignment: 'center',
                        locale: 'zh_TW',
                        width: 280,
                    });
                })
                .catch((error) => {
                    if (isCancelled) return;
                    onErrorRef.current(error.message || LOAD_ERROR_MESSAGE);
                });

            return () => {
                isCancelled = true;
            };
        },
        // eslint-disable-next-line
        [clientId],
    );

    return buttonRef;
};

export default useGoogleSignIn;
